/**
 * "Découvrir" (public events near me): filters, request params and the
 * message that explains how "Près de moi" was resolved. Pure functions,
 * aligned with `GET /events/discover` (keeptouch-api src/events).
 */
export const DISCOVERY_RADII = [10, 25, 50] as const
export type DiscoveryRadius = (typeof DISCOVERY_RADII)[number]
export const DISCOVERY_PAGE_SIZE = 20

export interface DiscoveryFilters {
  search: string
  nearMe: boolean
  radiusKm: DiscoveryRadius
}

export const defaultDiscoveryFilters: DiscoveryFilters = {
  search: '',
  nearMe: false,
  radiusKm: 25,
}

export interface DiscoveryParams {
  page: number
  limit: number
  search?: string
  near?: true
  radiusKm?: DiscoveryRadius
}

/** Empty values are left out so the URL (and the query cache key) stay minimal. */
export function buildDiscoveryParams(filters: DiscoveryFilters, page = 1): DiscoveryParams {
  const search = filters.search.trim()
  return {
    page,
    limit: DISCOVERY_PAGE_SIZE,
    ...(search ? { search } : {}),
    ...(filters.nearMe ? { near: true as const, radiusKm: filters.radiusKm } : {}),
  }
}

export interface DiscoveryNear {
  mode: 'coordinates' | 'city' | 'unknown'
  radiusKm: number | null
}

/** Which explanation to show under the filters; null when "Près de moi" is off. */
export function discoveryNotice(near: DiscoveryNear | null | undefined) {
  if (!near) return null
  if (near.mode === 'coordinates') return { key: 'locCoordinates' as const, km: near.radiusKm }
  return { key: near.mode === 'city' ? ('locCity' as const) : ('locUnknown' as const), km: null }
}

/** "3,4" / "12" — one decimal under 10 km, none above. */
export function formatDistanceKm(km: number, locale: string): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: km < 10 ? 1 : 0 }).format(km)
}
